import * as React from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import { Button } from '../primitives/Button';
import { cn } from '../lib/cn';

/**
 * PayNowButton — design-system button for PayFast checkout.
 *
 * Phase: shipped 2026-05-07 as part of Phase 9 PayFast Slice 9.1
 * (`_bmad-output/PHASE_9_PAYFAST_BRD.md` + architecture brief §1).
 *
 * This is a DUMB component. It does not import Supabase, it does not call
 * Edge Functions, it does not read siteConfig. The smart wrapping lives in
 * the calling page or hook (e.g. portal/src/domains/billing/hooks/
 * usePayFastInitiate). Mirrors the EftBankDetailsCard pattern — data and
 * behaviour injected via props.
 *
 * Usage:
 *
 *   const { initiate, isLoading, error } = usePayFastInitiate(invoice.id);
 *   <PayNowButton
 *     amountDisplay={formatZAR(invoice.totalZAR)}
 *     onClick={initiate}
 *     isLoading={isLoading}
 *     size="sm"
 *   />
 *
 * The `disabled` + `disabledReason` props are kept for surfaces that want to
 * render the button in a not-yet-payable state (e.g. draft invoices) without
 * branching their JSX.
 */

export interface PayNowButtonProps {
  /** Pre-formatted display amount, e.g. "R129.00". Caller controls locale. */
  amountDisplay: string;
  /** Click handler (typically the `initiate` from usePayFastInitiate). */
  onClick: () => void;
  /** Disables the button and shows a spinner; redirect to PayFast is in flight. */
  isLoading?: boolean;
  /** Disables the button without spinner; for not-yet-payable invoice states. */
  disabled?: boolean;
  /** Tooltip shown when `disabled` is true. */
  disabledReason?: string;
  /** Match the parent Button size. */
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

function PayNowButton({
  amountDisplay,
  onClick,
  isLoading,
  disabled,
  disabledReason,
  size = 'default',
  className,
}: PayNowButtonProps) {
  return (
    <Button
      type="button"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      title={disabled ? disabledReason : undefined}
      className={cn('gap-2', className)}
    >
      {isLoading ? (
        <Loader2 className="size-4 animate-spin" aria-hidden />
      ) : (
        <CreditCard className="size-4" aria-hidden />
      )}
      {isLoading ? 'Redirecting…' : `Pay now (${amountDisplay})`}
    </Button>
  );
}

export { PayNowButton };
